import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ROLE_COLORS = {
  admin: '#F85149', senior: '#C19A6B', junior: '#3FB950',
  manager: '#A371F7', client: '#58A6FF'
};

const MODULE_ACCENT = { tv: '#C19A6B', digital: '#00D4FF' };

export default function Layout({ module, setModule }) {
  const { user, logout, isAdmin, isManager } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isPlanEditor = /\/plans\/\d+/.test(location.pathname);

  // Sidebar visibility: always hide when entering plan editor
  const [sidebarVisible, setSidebarVisible] = useState(!isPlanEditor);
  const [collapsed, setCollapsed] = useState(false);

  // When route changes to/from plan editor, auto-hide/show
  useEffect(() => {
    if (isPlanEditor) {
      setSidebarVisible(false);
    } else {
      setSidebarVisible(true);
    }
  }, [isPlanEditor]);

  function handleLogout() { logout(); navigate('/login'); }
  function switchModule(m) { setModule(m); navigate('/'); }

  const accent = MODULE_ACCENT[module];

  const navStyle = ({ isActive }) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px', borderRadius: 7,
    textDecoration: 'none', fontSize: 13, fontWeight: 500,
    color: isActive ? accent : '#9A8A7A',
    background: isActive ? `${accent}18` : 'transparent',
    borderLeft: isActive ? `2px solid ${accent}` : '2px solid transparent',
    transition: 'all .15s', whiteSpace: 'nowrap', overflow: 'hidden',
  });

  const tvNav = [
    { to: '/', end: true, icon: '📊', label: 'Dashboard' },
    { to: '/clients', icon: '🏢', label: 'Clients' },
    ...(isAdmin || isManager ? [{ to: '/users', icon: '👥', label: 'Users' }] : []),
  ];
  const digitalNav = [
    { to: '/', end: true, icon: '📊', label: 'Dashboard' },
    { to: '/clients', icon: '🏢', label: 'Clients' },
    ...(isAdmin || isManager ? [{ to: '/users', icon: '👥', label: 'Users' }] : []),
  ];
  const navItems = module === 'tv' ? tvNav : digitalNav;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F5F5F5', position: 'relative' }}>

      {/* Floating tab to show sidebar when hidden */}
      {!sidebarVisible && (
        <button
          onClick={() => setSidebarVisible(true)}
          title="Show sidebar"
          style={{
            position: 'fixed', top: 56, left: 0, zIndex: 100,
            width: 20, height: 48,
            background: accent, border: 'none',
            borderRadius: '0 6px 6px 0',
            cursor: 'pointer', color: '#fff', fontSize: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '2px 0 8px rgba(0,0,0,0.2)',
          }}
        >›</button>
      )}

      {/* Sidebar */}
      {sidebarVisible && (
        <div style={{
          width: collapsed ? 52 : 220, flexShrink: 0,
          background: '#FFFFFF', borderRight: '1px solid #21262D',
          display: 'flex', flexDirection: 'column',
          transition: 'width .2s', overflow: 'hidden',
        }}>
          {/* Logo */}
          <div style={{ padding: '14px 12px', borderBottom: '1px solid #21262D', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, flexShrink: 0, background: accent, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#F5F5F5' }}>
              {module === 'tv' ? 'TV' : '⟡'}
            </div>
            {!collapsed && (
              <div>
                <div style={{ color: '#0A0A0A', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                  {module === 'tv' ? 'TV Planning' : 'Digital Planning'}
                </div>
                <div style={{ color: '#9A8A7A', fontSize: 10, fontFamily: 'IBM Plex Mono, monospace' }}>dataintelligence.md</div>
              </div>
            )}
          </div>

          {/* Module Switcher */}
          {!collapsed && (
            <div style={{ padding: '10px 10px 0' }}>
              <div style={{ display: 'flex', background: '#F5F5F5', border: '1px solid #21262D', borderRadius: 8, padding: 3, gap: 2 }}>
                <button onClick={() => switchModule('tv')} style={{ flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: module === 'tv' ? '#C19A6B' : 'transparent', color: module === 'tv' ? '#F5F5F5' : '#9A8A7A', transition: 'all .15s' }}>📺 TV</button>
                <button onClick={() => switchModule('digital')} style={{ flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: module === 'digital' ? '#00D4FF' : 'transparent', color: module === 'digital' ? '#F5F5F5' : '#9A8A7A', transition: 'all .15s' }}>⟡ Digital</button>
              </div>
            </div>
          )}

          {/* Nav */}
          <nav style={{ flex: 1, padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {navItems.map(({ to, end, icon, label }) => (
              <NavLink key={to} to={to} end={end} style={navStyle}>
                <span>{icon}</span>
                {!collapsed && <span>{label}</span>}
              </NavLink>
            ))}
          </nav>

          {/* User + buttons */}
          <div style={{ padding: '10px 8px', borderTop: '1px solid #21262D' }}>
            {!collapsed && user && (
              <div style={{ marginBottom: 8, padding: '8px 10px', background: '#F5F5F5', borderRadius: 7 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name || user.email}</div>
                <div style={{ fontSize: 11, color: ROLE_COLORS[user.role] || '#9A8A7A', marginTop: 2 }}>{user.role}</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 4 }}>
              {isPlanEditor ? (
                <button onClick={() => setSidebarVisible(false)} style={{ flex: 1, padding: '6px 10px', background: 'transparent', border: `1px solid ${accent}44`, borderRadius: 6, color: accent, cursor: 'pointer', fontSize: 12 }}>← Hide</button>
              ) : (
                <button onClick={() => setCollapsed(c => !c)} style={{ flex: collapsed ? 1 : 0, padding: '6px 10px', background: 'transparent', border: '1px solid #21262D', borderRadius: 6, color: '#9A8A7A', cursor: 'pointer', fontSize: 12 }}>
                  {collapsed ? '→' : '←'}
                </button>
              )}
              {!collapsed && (
                <button onClick={handleLogout} style={{ flex: 1, padding: '6px 10px', background: 'transparent', border: '1px solid #21262D', borderRadius: 6, color: '#9A8A7A', cursor: 'pointer', fontSize: 12 }}>Logout</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        <Outlet />
      </div>
    </div>
  );
}
